import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const parseHashParams = (hash: string): URLSearchParams => {
  const normalized = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(normalized);
};

const waitForRecoverySession = async (timeoutMs = 2500): Promise<boolean> => {
  return new Promise((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      subscription.unsubscribe();
      resolve(false);
    }, timeoutMs);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (settled) return;
      if ((event === "SIGNED_IN" || event === "PASSWORD_RECOVERY") && session) {
        settled = true;
        window.clearTimeout(timeout);
        subscription.unsubscribe();
        resolve(true);
      }
    });
  });
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isValidLink, setIsValidLink] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validateResetLink = async () => {
      const code = searchParams.get("code");

      if (code) {
        // PKCE flow: exchange authorization code for a session.
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;

        if (error) {
          // In dev StrictMode, code exchange may run twice and code can be consumed
          // by the first run. If a session exists, continue as valid.
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (cancelled) return;

          if (!session) {
            setError(
              "Invalid or expired reset link. Please request a new one.",
            );
            return;
          }
        }

        window.history.replaceState(null, "", window.location.pathname);
        setIsValidLink(true);
        return;
      }

      // Some Supabase recovery links provide tokens in URL query or hash.
      const hashParams = parseHashParams(window.location.hash);
      const accessToken =
        searchParams.get("access_token") || hashParams.get("access_token");
      const refreshToken =
        searchParams.get("refresh_token") || hashParams.get("refresh_token");
      const type = searchParams.get("type") || hashParams.get("type");

      if (type === "recovery" && accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (cancelled) return;

        if (error) {
          setError("Invalid or expired reset link. Please request a new one.");
          return;
        }

        // Remove sensitive hash params after session creation.
        window.history.replaceState(null, "", window.location.pathname);
        setIsValidLink(true);
        return;
      }

      // If a session already exists, allow password update.
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;

      if (session) {
        setIsValidLink(true);
        return;
      }

      // Allow brief time for async auth callback/session hydration.
      const hasRecoverySession = await waitForRecoverySession();
      if (cancelled) return;

      if (hasRecoverySession) {
        setIsValidLink(true);
        return;
      }

      setError("Invalid or expired reset link. Please request a new one.");
    };

    validateResetLink();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        setError(error.message);
      } else {
        toast({
          title: "Password updated successfully!",
          description: "You can now log in with your new password.",
        });

        // Redirect to home page after successful reset
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while updating your password");
    } finally {
      setLoading(false);
    }
  };

  if (!isValidLink && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#40e0d0] mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">
            Verifying reset link...
          </h2>
        </div>
      </div>
    );
  }

  if (error && !isValidLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Invalid Reset Link
            </h2>
            <p className="text-gray-600">{error}</p>
          </div>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#40e0d0] hover:bg-[#2bbdb3] text-white"
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/Logo.png"
              alt="My!hū"
              className="h-14 w-auto cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Reset Your Password
          </h2>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#40e0d0] text-gray-800"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>

          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#40e0d0] text-gray-800"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <Button
            type="submit"
            className="w-full bg-[#40e0d0] hover:bg-[#2bbdb3] text-white"
            disabled={loading}
          >
            {loading ? "Updating Password..." : "Update Password"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-[#40e0d0] hover:underline text-sm"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
